import { useEffect, useRef, useState } from 'react';

/**
 * useScrollReveal
 * Observes an element and returns whether it has entered the viewport.
 *
 * @param {Object} options
 * @param {number} options.threshold - 0..1, fraction visible to trigger (default 0.15)
 * @param {string} options.rootMargin - IntersectionObserver rootMargin (default '0px 0px -60px 0px')
 * @param {boolean} options.once - if true, stops observing after first reveal (default true)
 */
export function useScrollReveal({
  threshold = 0.15,
  rootMargin = '0px 0px -60px 0px',
  once = true,
} = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Respect reduced-motion — show everything immediately
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}
